import { getContext, hasContext, setContext, onMount } from 'svelte';
import { derived, readable, writable } from 'svelte/store';

let instanceCounter = 0;

/**
 * Manages a stack under a given `key`. If there is no Svelte context given for that `key`,
 * a context for that `key` is created. If a `value` is given, it will be pushed to the stack
 * as soon as the component is mounted. When the component is destroyed, the value is removed
 * from the stack. The ordering of values pushed to the stack remains preserved.
 *
 * @param {unknown} key The key to use for the Svelte context.
 * @param {unknown} value The value or store from which to read the value to be pushed to the stack.
 * If it is a store, the value inside the stack is kept in sync with the value of the store.
 * @returns {import('svelte/store').Readable} Returns a readable store containing the top most value of the stack.
 */
export default function useStack(key, value = undefined) {
  if (key === undefined) throw new Error("'key' must not be undefined");

  const lot = instanceCounter++;
  /** @type {import('svelte/store').Writable<Map<number,unknown>>} */
  const stackStore = hasContext(key) ? getContext(key) : setContext(key, writable(new Map()));

  if (value !== undefined) {
    onMount(() => {
      const isStore = typeof value?.subscribe === 'function';
      /** @type {import('svelte/store').Readable<unknown>} */
      const valueStore = isStore ? value : readable(value);
      const unsubscribe = valueStore.subscribe((v) => {
        stackStore.update((map) => {
          map.set(lot, v);
          return map;
        });
      });

      return () => {
        unsubscribe();
        stackStore.update((map) => {
          map.delete(lot);
          return map;
        });
      };
    });
  }

  return derived(
    stackStore,
    ($stackStore) =>
      [...$stackStore.entries()].sort((a, b) => b[0] - a[0]).map((entry) => entry[1])[0]
  );
}
